import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { QUALITY_TIMELINE } from '../consts';
import { useEffectiveVisibleRange } from '../store/useSyncViewStore';
import { TimelineAxisStrip } from '../timeline/axis/TimelineAxisStrip';
import { TimelineChart } from '../timeline/chart/TimelineChart';
import { StreamQualityReportResponseDto } from '../stream-quality.types';

export type TimezoneGroupSectionProps = {
  report: StreamQualityReportResponseDto;
  width: number;
  /** Pre-formatted offset pair label (e.g. UTC-5 or UTC-5 → UTC-4). Station count is appended in parentheses. */
  groupLabel: string;
  stationIds: string[];
  /** First sorted IANA zone in the bucket; tick labels use this wall time. */
  groupTimeZone: string;
  /** `top` offset so sticky group chrome (title row + axis) sits below the global report header. */
  stickyAxisTop: number;
  /** When true, omit only the chevron + group name column; sticky per-group selected-range axis stays visible. Used when there is a single timezone bucket. */
  hideGroupTitleRow?: boolean;
};

/**
 * Collapsible block for one UTC-offset bucket: timeline rows for its stations only.
 * Sticky bar: title control in the same left gutter width as `TimelineChart` (`QUALITY_TIMELINE.margin.left`),
 * then the selected-range axis (`TimelineAxisStrip` with `trimLeftGutter` so ticks align without clipping; hidden when collapsed).
 * Only the chart is inside `Collapse` so MUI’s overflow handling does not break sticky positioning.
 * Expanded/collapsed state is local to this section (default expanded).
 * With `hideGroupTitleRow`, only the chevron + label are omitted; the sticky per-group axis and chart remain (no collapse).
 */
export function TimezoneGroupSection({
  report,
  width,
  groupLabel,
  stationIds,
  groupTimeZone,
  stickyAxisTop,
  hideGroupTitleRow = false,
}: TimezoneGroupSectionProps) {
  const [expanded, setExpanded] = useState(true);
  const selectedRange = useEffectiveVisibleRange(report);

  const toggle = () => {
    setExpanded((prev) => !prev);
  };

  if (hideGroupTitleRow) {
    return (
      <Box>
        <Box
          sx={(theme) => ({
            position: 'sticky',
            top: stickyAxisTop,
            zIndex: theme.zIndex.appBar - 2,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 0,
            minHeight: 40,
            pt: 1,
            pb: '2px',
          })}
        >
          {width > 0 ? (
            <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
              <TimelineAxisStrip width={width} timeRange={selectedRange} timeZone={groupTimeZone} />
            </Box>
          ) : null}
        </Box>
        <Box sx={{ mt: 1 }}>
          <TimelineChart
            width={width}
            report={report}
            includeTopAxis={false}
            stationIds={stationIds}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={(theme) => ({
          position: 'sticky',
          top: stickyAxisTop,
          zIndex: theme.zIndex.appBar - 2,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          minHeight: 40,
          py: 1,
        })}
      >
        <Box
          component="button"
          type="button"
          onClick={toggle}
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1,
            flex: '0 0 auto',
            width: QUALITY_TIMELINE.margin.left,
            minWidth: QUALITY_TIMELINE.margin.left,
            maxWidth: QUALITY_TIMELINE.margin.left,
            minHeight: 40,
            boxSizing: 'border-box',
            pr: 0.5,
            textAlign: 'left',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            padding: 0,
            font: 'inherit',
            color: 'text.primary',
          }}
        >
          <ExpandMoreIcon
            fontSize="small"
            color="action"
            sx={{
              flexShrink: 0,
              transform: !expanded ? 'rotate(270deg)' : 'none',
            }}
          />
          <Typography variant="subtitle2" component="span" noWrap sx={{ minWidth: 0 }}>
            {groupLabel}{' '}
            <Typography variant="subtitle2" color="text.secondary" component="span" ml={0.5}>
              ({stationIds.length})
            </Typography>
          </Typography>
        </Box>
        {expanded && width > 0 ? (
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              alignSelf: 'stretch',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <TimelineAxisStrip
              width={width}
              timeRange={selectedRange}
              timeZone={groupTimeZone}
              trimLeftGutter
            />
          </Box>
        ) : null}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ mt: 1 }}>
          <TimelineChart
            width={width}
            report={report}
            includeTopAxis={false}
            stationIds={stationIds}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
